import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { usePost } from '../API';
import type { NotificationSummary, NotificationRecipientsResult } from '@aidkitorg/aidkit/lib/notifications/NotificationService';
import { logAdminPanelError } from '../utils/adminPanel.utils';

type NotificationsContextType = {
  notifications: Array<NotificationSummary>
  enabledNotifications: number,
  disabledNotifications: number,
  notificationsDataTimestamp?: number,

  isNotificationsLoading: boolean,
  getIsRecipientsLoading: (targetFieldPrefix: string) => boolean,
  
  fetchNotificationsOverview: (force?: boolean) => void,
  fetchNotificationRecipients: (targetFieldPrefix: string, force?: boolean) => Promise<void>,
  getNotificationRecipients: (targetFieldPrefix: string) => NotificationRecipientsResult | null,
  getRecipientsTimestamp: (targetFieldPrefix: string) => number | undefined
}

type NotificationsOverviewRef = {
  notifications?: Array<NotificationSummary>,
  timestamp?: number,
  expiresAt?: number,
}

type NotificationRecipientsRef = {
  data?: NotificationRecipientsResult,
  timestamp?: number,
  expiresAt?: number,
}

type NotificationsProviderProps = {
  children: React.ReactNode
}

const NotificationsContext = createContext<NotificationsContextType>({
  notifications: [],
  enabledNotifications: 0,
  disabledNotifications: 0,

  isNotificationsLoading: false,
  getIsRecipientsLoading: () => false,

  fetchNotificationsOverview: async (_force?: boolean) => {},
  fetchNotificationRecipients: async (_targetFieldPrefix: string, _force?: boolean) => {},
  getNotificationRecipients: () => null,
  getRecipientsTimestamp: () => undefined,
})

export const useNotifications = () => {
  const context = useContext(NotificationsContext)
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationsProvider")
  }

  return context;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Array<NotificationSummary>>([])
  const [enabledNotifications, setEnabledNotifications] = useState(0)
  const [disabledNotifications, setDisabledNotifications] = useState(0)
  const [notificationsDataTimestamp, setNotificationsDataTimestamp] = useState<number>()

  const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);
  const [isRecipientsLoading, setIsRecipientsLoading] = useState<{ [targetFieldPrefix: string]: boolean }>({})

  const notificationsRef = useRef<NotificationsOverviewRef>({})
  const notificationRecipientsRef = useRef<{ [targetFieldPrefix: string]: NotificationRecipientsRef }>({})
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const getNotificationsData = usePost("/api/admin_panel/notifications")
  const getNotificationsStatus = usePost("/api/admin_panel/notifications/status")
  const getNotificationRecipientsData = usePost("/api/admin_panel/notifications/recipients")

  const pollNotificationsStatus = useCallback(async (taskId: string) => {
    const result = await getNotificationsStatus({ taskId })

    if (result && result.success && result.data) {
      if (result.data.status === 'completed') {
        // Job completed, update state with final data
        notificationsRef.current = {
          notifications: result.data.notifications,
          timestamp: result.data.timestamp,
          expiresAt: result.data.expiresAt
        }

        setEnabledNotifications(result.data.totalEnabledNotifications)
        setDisabledNotifications(result.data.totalDisabledNotifications)
        setNotifications(result.data.notifications)
        setNotificationsDataTimestamp(result.data.timestamp)
        setIsNotificationsLoading(false)

        // Clear polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }

        return true // Job completed
      } else if (result.data.jobStatus === 'FAILED') {
        setIsNotificationsLoading(false)
        logAdminPanelError(result.error || { code: 500, message: 'Job failed', statusCode: 500 }, 'Notification generation failed')

        // Clear polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }

        return true // Job failed, stop polling
      }
      // else status is still 'pending', keep polling
    } else if (result.error) {
      setIsNotificationsLoading(false)
      logAdminPanelError(result.error, result.message)

      // Clear polling interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }

      return true // Error, stop polling
    }

    return false // Continue polling
  }, [getNotificationsStatus])

  const fetchNotificationsOverview = useCallback(async (force?: boolean) => {
    const now = Date.now()

    // Check if an unexpired value lives in the cache
    if (!force &&
      notificationsRef.current.notifications &&
      (notificationsRef.current.expiresAt && now < notificationsRef.current.expiresAt))
    {
      return
    }

    setIsNotificationsLoading(true)
    const result = await getNotificationsData({ force })

    if (result && result.success && result.data) {
      if (result.data.status === 'pending' && result.data.taskId) {
        // Job is pending, start polling
        const taskId = result.data.taskId

        // Clear any existing polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
        }

        // Poll immediately once
        const completed = await pollNotificationsStatus(taskId)

        if (!completed) {
          // Start polling every 2 seconds
          pollingIntervalRef.current = setInterval(async () => {
            await pollNotificationsStatus(taskId)
          }, 2000)
        }
      } else {
        // Data ready immediately (from cache)
        setIsNotificationsLoading(false)

        notificationsRef.current = {
          notifications: result.data.notifications,
          timestamp: result.data.timestamp,
          expiresAt: result.data.expiresAt
        }

        setEnabledNotifications(result.data.totalEnabledNotifications)
        setDisabledNotifications(result.data.totalDisabledNotifications)
        setNotifications(result.data.notifications)
        setNotificationsDataTimestamp(result.data.timestamp)
      }
    } else if (result.error) {
      setIsNotificationsLoading(false)
      logAdminPanelError(result.error, result.message)
    }
  }, [getNotificationsData, pollNotificationsStatus])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [])

  const fetchNotificationRecipients = useCallback(async (targetFieldPrefix: string, force?: boolean) => {
    const now = Date.now()

    // Check if an unexpired value lives in the cache
    if (!force && 
      notificationRecipientsRef.current[targetFieldPrefix]?.data && 
      (notificationRecipientsRef.current[targetFieldPrefix]?.expiresAt && now < notificationRecipientsRef.current[targetFieldPrefix].expiresAt!)) 
    {
      return
    }

    setIsRecipientsLoading(prev => ({ ...prev, [targetFieldPrefix]: true }))
    
    try {
      const result = await getNotificationRecipientsData({ targetFieldPrefix })

      if (result && result.success && result.data) {
        notificationRecipientsRef.current[targetFieldPrefix] = { 
          data: result.data, 
          timestamp: Date.now(), 
          expiresAt: Date.now() + 300000 // 5 minute cache
        }
      } else if (result.error) {
        logAdminPanelError(result.error, result.message)
      }
    } catch (error) {
      logAdminPanelError(error as any, 'Failed to fetch notification recipients')
    } finally {
      setIsRecipientsLoading(prev => ({ ...prev, [targetFieldPrefix]: false }))
    }
  }, [getNotificationRecipientsData])

  const getNotificationRecipients = useCallback((targetFieldPrefix: string): NotificationRecipientsResult | null => {
    return notificationRecipientsRef.current[targetFieldPrefix]?.data || null
  }, [])

  const getRecipientsTimestamp = useCallback((targetFieldPrefix: string): number | undefined => {
    return notificationRecipientsRef.current[targetFieldPrefix]?.timestamp
  }, [])

  const getIsRecipientsLoading = useCallback((targetFieldPrefix: string) => {
    return isRecipientsLoading[targetFieldPrefix] || false
  }, [isRecipientsLoading])


  const value = {
    notifications,
    enabledNotifications,
    disabledNotifications,
    notificationsDataTimestamp,

    isNotificationsLoading,
    getIsRecipientsLoading,

    fetchNotificationsOverview,
    fetchNotificationRecipients,
    getNotificationRecipients,
    getRecipientsTimestamp,
  }

  return (
    <NotificationsContext.Provider value={ value }>
      {children}
    </NotificationsContext.Provider>
  )
}
